import React, { useState, useEffect } from 'react';
import {
    Container, Header, Box, SpaceBetween, Button, Table, Modal, FormField,
    Input, Alert
} from '@amzn/awsui-components-react';
import { useSelector } from 'react-redux';
import CustomSelectMultiAlias from '../Templates/components/CustomSelectMultiAlias';
import CustomMultiHybridInputAndSelect from '../Templates/components/CustomMultiHybridInputAndSelect';
import { useSuggestedLogins } from 'src/hooks/useSuggestedLogins';
import {
    createAdhocTask, listAdhocTasks, deleteAdhocTask, confirmAdhocTask
} from './api';

// AdHoc tasks: dashboard-level, confirmation-only deliverables (no upload - the
// uploader just confirms the task is done). Reviewers are shown for visibility.
// Props: entityId (required) + entityType (the adhoc_entity_types name, e.g.
// 'DASHBOARD' | 'APP'; defaults to 'DASHBOARD'). The task is associated with
// (entityType, entityId).
export default (props) => {
    const entityId = props.entityId;
    const entityType = props.entityType || 'DASHBOARD';
    const userId = useSelector((s) => s.auth.userId);
    const { suggestedLogins, aliasesLoading, getSuggestedLoginsCaller } = useSuggestedLogins();

    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Create-form state
    const [taskName, setTaskName] = useState('');
    const [description, setDescription] = useState('');
    const [uploaders, setUploaders] = useState([]);
    const [reviewers, setReviewers] = useState([]);
    const [saving, setSaving] = useState(false);
    // The alias pickers (CustomSelectMultiAlias / CustomMultiHybridInputAndSelect)
    // seed their internal rows from getValues only once on mount and don't re-read
    // parent state. Bumping this key remounts the create form so cleared
    // uploaders/reviewers actually reset in the UI after a successful save.
    const [formKey, setFormKey] = useState(0);

    // Confirm-modal state
    const [confirmTask, setConfirmTask] = useState(null);
    const [quarter, setQuarter] = useState(props.quarter || '');
    const [trackingType, setTrackingType] = useState('PreWB');
    const [confirming, setConfirming] = useState(false);

    useEffect(() => { loadTasks(); }, [entityId, entityType]);

    const loadTasks = async () => {
        if (!entityId) return;
        setLoading(true); setError('');
        try {
            const data = await listAdhocTasks(entityType, entityId);
            // Surface a backend structured { error } instead of silently showing
            // an empty table (consistent with create/delete/confirm).
            if (data && data.error) { setError(data.error); return; }
            setTasks(Array.isArray(data) ? data : []);
        } catch (e) {
            setError('Could not load AdHoc tasks');
        } finally {
            setLoading(false);
        }
    };

    // CustomSelectMultiAlias adapters (field-based get/update).
    const getValues = (field) => (field === 'uploaders' ? uploaders : reviewers);
    const updateValue = (field, value) => {
        if (field === 'uploaders') setUploaders(value); else setReviewers(value);
    };
    // Reviewer "type" select options, mirroring FRISCO/FLEXICO approvers
    // (USER -> APPROVER, LDAP -> APPROVER_LDAP). Level is captured separately
    // via column_index (0 = reviewer, 1 = approver, ...).
    const getReviewerTypeOptions = () => ([
        { label: 'USER', value: 'APPROVER' },
        { label: 'LDAP', value: 'APPROVER_LDAP' },
    ]);

    const addTask = async () => {
        setSaving(true); setError('');
        const input = {
            task_name: taskName.trim(),
            entity_type: entityType,
            entity_id: `${entityId}`,
            description: description.trim(),
            created_by: userId,
            uploaders: uploaders.map((a) => ({ alias: a, type: 'UPLOADER' })),
            // reviewers are already {alias, type, column_index} objects
            reviewers: reviewers.filter((r) => r && r.alias),
        };
        try {
            const res = await createAdhocTask(input);
            // The API resolves with the payload even when the backend returns a
            // structured { error } (authz / validation) - surface it instead of
            // silently treating it as success.
            if (res && res.error) { setError(res.error); return; }
            setTaskName(''); setDescription(''); setUploaders([]); setReviewers([]);
            setFormKey((k) => k + 1); // remount the alias inputs so they re-read cleared state
            await loadTasks();
        } catch (e) {
            setError('Could not create AdHoc task');
        } finally {
            setSaving(false);
        }
    };

    const removeTask = async (task_id) => {
        setLoading(true); setError('');
        try {
            const res = await deleteAdhocTask(task_id);
            if (res && res.error) { setLoading(false); setError(res.error); return; }
            await loadTasks();
        } catch (e) {
            setLoading(false); setError('Could not delete task');
        }
    };

    const submitConfirm = async () => {
        setConfirming(true); setError('');
        try {
            const res = await confirmAdhocTask({
                task_id: `${confirmTask.task_id}`,
                entity_type: entityType,
                entity_id: `${entityId}`,
                quarter: quarter.trim(),
                tracking_type: trackingType,
                confirmed_by: userId,
                task_name: confirmTask.task_name,
            });
            if (res && res.error) { setError(res.error); return; }
            setConfirmTask(null);
            await loadTasks();
        } catch (e) {
            setError('Could not confirm task');
        } finally {
            setConfirming(false);
        }
    };

    const aliasList = (arr) => (arr || []).map((a) => a.alias || a).join(', ');
    // Reviewers show their level (column_index): 0 = reviewer, 1 = approver, ...
    const reviewerList = (arr) => (arr || [])
        .slice()
        .sort((a, b) => (Number(a?.column_index) || 0) - (Number(b?.column_index) || 0))
        .map((r) => `${r.alias} (L${Number(r?.column_index) || 0})`)
        .join(', ');

    return (
        <Container header={<Header variant="h2"
            actions={<Button iconName="refresh" variant="icon" onClick={loadTasks} />}
            description="Dashboard tasks that only require a completion confirmation (no upload)">
            AdHoc tasks
        </Header>}>
            <SpaceBetween size="l">
                {error ? <Alert type="error">{error}</Alert> : null}

                {/* Create */}
                <Box key={formKey}>
                    <SpaceBetween size="s">
                        <FormField label="Task name">
                            <Input value={taskName} placeholder="e.g. Confirm quarterly reconciliation reviewed"
                                onChange={({ detail }) => setTaskName(detail.value)} />
                        </FormField>
                        <FormField label="Description">
                            <Input value={description} onChange={({ detail }) => setDescription(detail.value)} />
                        </FormField>
                        <FormField label="Uploaders (confirm the task)">
                            <CustomSelectMultiAlias field="uploaders" getValues={getValues} updateValue={updateValue}
                                options={suggestedLogins} getOptions={getSuggestedLoginsCaller} aliasesLoading={aliasesLoading} />
                        </FormField>
                        <FormField label="Reviewers (visibility only)"
                            description="Order of approval: level 0 = reviewer, level 1 = approver (matches FRISCO/FLEXICO)">
                            <CustomMultiHybridInputAndSelect
                                field="reviewers"
                                inputKey="alias" selectKey="type" selectHint="Choose type"
                                placeholder="alias"
                                needIndex={true} indexKey="column_index" indexPlaceHolder="Order of approval" defIndex={0}
                                getValues={getValues} updateValue={updateValue}
                                getOptions={getReviewerTypeOptions}
                                loginOptions={suggestedLogins} getLoginOptions={getSuggestedLoginsCaller}
                                aliasesLoading={aliasesLoading}
                                duplicatesNotAllowed={true} info=' ' />
                        </FormField>
                        <Box float="right">
                            <Button variant="primary" loading={saving}
                                disabled={!taskName.trim() || uploaders.length === 0}
                                onClick={addTask}>Add task</Button>
                        </Box>
                    </SpaceBetween>
                </Box>

                {/* List */}
                <Table
                    loading={loading}
                    loadingText="Loading tasks..."
                    items={tasks}
                    columnDefinitions={[
                        { id: 'task_name', header: 'Task', cell: (t) => t.task_name },
                        { id: 'description', header: 'Description', cell: (t) => t.description },
                        { id: 'uploaders', header: 'Uploaders', cell: (t) => aliasList(t.uploaders) },
                        { id: 'reviewers', header: 'Reviewers (by level)', cell: (t) => reviewerList(t.reviewers) },
                        {
                            id: 'actions', header: 'Actions', cell: (t) => (
                                <SpaceBetween direction="horizontal" size="xs">
                                    <Button variant="inline-link" onClick={() => { setError(''); setConfirmTask(t); }}>Mark done</Button>
                                    <Button variant="inline-link" iconName="remove" onClick={() => removeTask(t.task_id)}>Delete</Button>
                                </SpaceBetween>
                            )
                        },
                    ]}
                    empty={<Box textAlign="center">No AdHoc tasks</Box>}
                    variant="embedded"
                />
            </SpaceBetween>

            {/* Confirm modal: "are you sure this task is done? OK" */}
            <Modal
                visible={!!confirmTask}
                onDismiss={() => setConfirmTask(null)}
                header="Confirm task completion"
                footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
                    <Button variant="link" onClick={() => setConfirmTask(null)}>Cancel</Button>
                    <Button variant="primary" loading={confirming} disabled={!quarter.trim()} onClick={submitConfirm}>
                        Yes, mark done
                    </Button>
                </SpaceBetween></Box>}
            >
                <SpaceBetween size="m">
                    {error ? <Alert type="error">{error}</Alert> : null}
                    <Box>Are you sure the task <b>{confirmTask?.task_name}</b> is done?</Box>
                    <FormField label="Quarter" description="e.g. Q2 2026">
                        <Input value={quarter} onChange={({ detail }) => setQuarter(detail.value)} />
                    </FormField>
                    <FormField label="WB interval">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant={trackingType === 'PreWB' ? 'primary' : 'normal'} onClick={() => setTrackingType('PreWB')}>Pre-WB</Button>
                            <Button variant={trackingType === 'PostWB' ? 'primary' : 'normal'} onClick={() => setTrackingType('PostWB')}>Post-WB</Button>
                        </SpaceBetween>
                    </FormField>
                </SpaceBetween>
            </Modal>
        </Container>
    );
};
